<div class="image-cropper-modal">
  <div class="image-cropper-modal__header">
    <div class="header-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <path d="M7 2h2v4h9v9h4v2h-4v5h-2v-5H6V8H2V6h4V2h1zm2 6v7h7V8H9z"/>
      </svg>
    </div>
    <div>
      <h6 class="modal-title">{{ title | translate }}</h6>
      <p>{{ 'Adjust the image to fit the required size.' | translate }}</p>
    </div>
    <button
      type="button"
      class="modal-close"
      aria-label="Close"
      (click)="cancel()"
    >
      <span aria-hidden="true">×</span>
    </button>
  </div>

  <div class="image-cropper-modal__body">
    <div class="cropper-toolbar">
      <input
        #replacementImageInput
        type="file"
        [accept]="acceptedImageAccept"
        class="d-none"
        (change)="onReplacementImageSelected($event)"
      />
      <button type="button" class="tool-btn" (click)="openReplacementImagePicker()">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z"/></svg>
        {{ 'Re-upload' | translate }}
      </button>
      <span class="toolbar-separator" aria-hidden="true"></span>
      <button
        type="button"
        class="tool-btn"
        [class.tool-btn--active]="moveMode === 'selection'"
        (click)="setMoveMode('selection')"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm10-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm10-2h8v8h-8v-8zm2 2v4h4v-4h-4z"/></svg>
        {{ 'Move Selection' | translate }}
      </button>
      <button
        type="button"
        class="tool-btn"
        [class.tool-btn--active]="moveMode === 'image'"
        (click)="setMoveMode('image')"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M13 1.07V9h7v11H4V9h2V6.5c0-1.1.9-2 2-2s2 .9 2 2V9h1V4.5c0-1.1.9-2 2-2s2 .9 2 2V9h1V6.5c0-1.1.9-2 2-2s2 .9 2 2V14c0 2.21-1.79 4-4 4H9.83l-3.6 3.6L5 20.77 7.77 18H13c1.1 0 2-.9 2-2V9h-2V6.5c0-.28-.22-.5-.5-.5s-.5.22-.5.5V11h-2V4.5c0-.28-.22-.5-.5-.5s-.5.22-.5.5V11h-2V6.5c0-.28-.22-.5-.5-.5S13 6.22 13 6.5V1.07z"/></svg>
        {{ 'Move Image' | translate }}
      </button>
      <div class="zoom-control">
        <button
          type="button"
          class="zoom-control__btn"
          [disabled]="!isEditorReady || isApplying || isLoading || zoomValue <= minZoom"
          [attr.aria-label]="'Zoom out' | translate"
          (click)="zoomOut()"
        >
          <span aria-hidden="true">−</span>
        </button>
        <input
          type="range"
          [min]="minZoom"
          [max]="maxZoom"
          [step]="zoomStep"
          [ngModel]="zoomValue"
          (ngModelChange)="onZoomChange($event)"
          [attr.aria-label]="'Zoom level' | translate"
        />
        <button
          type="button"
          class="zoom-control__btn"
          [disabled]="!isEditorReady || isApplying || isLoading || zoomValue >= maxZoom"
          [attr.aria-label]="'Zoom in' | translate"
          (click)="zoomIn()"
        >
          <span aria-hidden="true">+</span>
        </button>
      </div>
      <button type="button" class="tool-btn" (click)="rotateLeft()">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7.11 8.53L5.7 7.11 2 11l3.7 3.89 1.41-1.42L5.83 13H13a4 4 0 0 1 0 8H6v2h7a6 6 0 0 0 0-12H5.83l1.28-1.47z"/></svg>
        {{ 'Rotate Left' | translate }}
      </button>
      <button type="button" class="tool-btn" (click)="rotateRight()">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M16.89 8.53L18.3 7.11 22 11l-3.7 3.89-1.41-1.42L18.17 13H11a4 4 0 0 0 0 8h7v2h-7a6 6 0 0 1 0-12h7.17l-1.28-1.47z"/></svg>
        {{ 'Rotate Right' | translate }}
      </button>
      <button type="button" class="tool-btn" (click)="resetAdjustments()">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5V2L8 6l4 4V7a5 5 0 1 1-4.58 3H5.26A7 7 0 1 0 12 5z"/></svg>
        {{ 'Reset' | translate }}
      </button>
    </div>

    <div *ngIf="isBelowRecommendedResolution" class="small-image-warning">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
      </svg>
      <span>{{ 'Uploaded image resolution is very small. For better visibility, upload an image at least {{size}} or larger.' | translate:{ size: targetSizeLabel } }}</span>
    </div>

    <div class="cropper-main">
      <div class="cropper-editor">
        <div
          #workspace
          class="cropper-workspace"
          [class.cropper-workspace--canvas-light]="canvasMode === 'light'"
          [class.cropper-workspace--canvas-dark]="canvasMode === 'dark'"
          [class.cropper-workspace--canvas-transparent]="canvasMode === 'transparent'"
          [class.cropper-workspace--move-selection]="moveMode === 'selection'"
          [class.cropper-workspace--move-image]="moveMode === 'image'"
          [class.cropper-workspace--dragging]="isDragging"
          [class.cropper-workspace--resize-nw]="activeResizeHandle === 'nw' || activeResizeHandle === 'se'"
          [class.cropper-workspace--resize-ne]="activeResizeHandle === 'ne' || activeResizeHandle === 'sw'"
          (wheel)="onWorkspaceWheel($event)"
          (pointerdown)="onPointerDown($event)"
          (pointermove)="onPointerMove($event)"
          (pointerup)="onPointerUp($event)"
          (pointercancel)="onPointerUp($event)"
        >
          <canvas #stageCanvas class="cropper-canvas" aria-hidden="true"></canvas>
          <div
            *ngIf="allowLogoShapeSwitch && !roundCropper"
            class="logo-shape-switch"
            role="tablist"
            [attr.aria-label]="'Logo shape' | translate"
            (pointerdown)="onLogoShapeSwitchPointerDown($event)"
          >
            <button
              type="button"
              role="tab"
              class="logo-shape-switch__tab"
              [class.logo-shape-switch__tab--active]="logoShapeMode === 'square'"
              [attr.aria-selected]="logoShapeMode === 'square'"
              (click)="setLogoShapeMode('square')"
            >
              {{ 'Square' | translate }}
            </button>
            <button
              type="button"
              role="tab"
              class="logo-shape-switch__tab"
              [class.logo-shape-switch__tab--active]="logoShapeMode === 'rectangle'"
              [attr.aria-selected]="logoShapeMode === 'rectangle'"
              (click)="setLogoShapeMode('rectangle')"
            >
              {{ 'Rectangle' | translate }}
            </button>
          </div>
          <div
            class="canvas-mode-switch"
            role="tablist"
            [attr.aria-label]="'Canvas background' | translate"
            (pointerdown)="onCanvasModeSwitchPointerDown($event)"
          >
            <button
              type="button"
              role="tab"
              class="canvas-mode-switch__tab"
              [class.canvas-mode-switch__tab--active]="canvasMode === 'light'"
              [attr.aria-selected]="canvasMode === 'light'"
              [attr.aria-label]="'Light canvas' | translate"
              [title]="'Light canvas' | translate"
              (click)="setCanvasMode('light')"
            >
              <span class="material-symbols-outlined" aria-hidden="true">light_mode</span>
            </button>
            <button
              type="button"
              role="tab"
              class="canvas-mode-switch__tab"
              [class.canvas-mode-switch__tab--active]="canvasMode === 'dark'"
              [attr.aria-selected]="canvasMode === 'dark'"
              [attr.aria-label]="'Dark canvas' | translate"
              [title]="'Dark canvas' | translate"
              (click)="setCanvasMode('dark')"
            >
              <span class="material-symbols-outlined" aria-hidden="true">dark_mode</span>
            </button>
            <button
              type="button"
              role="tab"
              class="canvas-mode-switch__tab"
              [class.canvas-mode-switch__tab--active]="canvasMode === 'transparent'"
              [attr.aria-selected]="canvasMode === 'transparent'"
              [attr.aria-label]="'Transparent canvas' | translate"
              [title]="'Transparent canvas' | translate"
              (click)="setCanvasMode('transparent')"
            >
              <span class="material-symbols-outlined" aria-hidden="true">opacity</span>
            </button>
          </div>
          <div *ngIf="isLoading || !isEditorReady" class="cropper-loading">
            {{ 'Loading image...' | translate }}
          </div>
          <div
            class="nudge-pad"
            [class.nudge-pad--on-light]="canvasMode === 'light' || canvasMode === 'transparent'"
            [class.nudge-pad--on-dark]="canvasMode === 'dark'"
            role="group"
            [attr.aria-label]="'Precise move controls' | translate"
            (pointerdown)="onCanvasModeSwitchPointerDown($event)"
          >
            <span class="nudge-pad__spacer" aria-hidden="true"></span>
            <button
              type="button"
              class="nudge-pad__btn"
              [class.nudge-pad__btn--active]="activeNudgeDirection === 'up'"
              [disabled]="!isEditorReady || isApplying || isLoading"
              [attr.aria-label]="'Nudge up' | translate"
              (pointerdown)="onNudgePointerDown($event, 'up')"
              (pointerup)="onNudgePointerUp($event)"
              (pointercancel)="onNudgePointerUp($event)"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 14.5L12 8.5l6 6"/></svg>
            </button>
            <span class="nudge-pad__spacer" aria-hidden="true"></span>
            <button
              type="button"
              class="nudge-pad__btn"
              [class.nudge-pad__btn--active]="activeNudgeDirection === 'left'"
              [disabled]="!isEditorReady || isApplying || isLoading"
              [attr.aria-label]="'Nudge left' | translate"
              (pointerdown)="onNudgePointerDown($event, 'left')"
              (pointerup)="onNudgePointerUp($event)"
              (pointercancel)="onNudgePointerUp($event)"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.5 6L8.5 12l6 6"/></svg>
            </button>
            <span class="nudge-pad__center" aria-hidden="true"></span>
            <button
              type="button"
              class="nudge-pad__btn"
              [class.nudge-pad__btn--active]="activeNudgeDirection === 'right'"
              [disabled]="!isEditorReady || isApplying || isLoading"
              [attr.aria-label]="'Nudge right' | translate"
              (pointerdown)="onNudgePointerDown($event, 'right')"
              (pointerup)="onNudgePointerUp($event)"
              (pointercancel)="onNudgePointerUp($event)"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9.5 6l6 6-6 6"/></svg>
            </button>
            <span class="nudge-pad__spacer" aria-hidden="true"></span>
            <button
              type="button"
              class="nudge-pad__btn"
              [class.nudge-pad__btn--active]="activeNudgeDirection === 'down'"
              [disabled]="!isEditorReady || isApplying || isLoading"
              [attr.aria-label]="'Nudge down' | translate"
              (pointerdown)="onNudgePointerDown($event, 'down')"
              (pointerup)="onNudgePointerUp($event)"
              (pointercancel)="onNudgePointerUp($event)"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9.5l6 6 6-6"/></svg>
            </button>
            <span class="nudge-pad__spacer" aria-hidden="true"></span>
          </div>
        </div>
        <div class="cropper-tip-bar">
          <span class="tip-info-btn" aria-hidden="true">
            <span class="tip-info-btn__label">i</span>
          </span>
          <span>{{ 'Tip: Use Move Selection or Move Image to adjust position. Use the arrow pad or keyboard arrows for 1 px precision. Drag corner handles or use the zoom slider to resize.' | translate }}</span>
        </div>
        <div *ngIf="showTransparencyOption" class="cropper-transparency-option">
          <label
            class="cropper-transparency-option__label"
            [class.cropper-transparency-option__label--readonly]="isTransparencyCheckboxReadOnly"
          >
            <input
              type="checkbox"
              class="cropper-transparency-option__checkbox"
              [checked]="keepLogoTransparency"
              [disabled]="isTransparencyCheckboxReadOnly"
              [attr.aria-readonly]="isTransparencyCheckboxReadOnly"
              (change)="onKeepTransparencyCheckboxChange($event)"
            />
            <span>{{ 'Keep the logo transparency' | translate }}</span>
          </label>
        </div>
      </div>

      <aside class="preview-panel">
        <h6 class="preview-heading">
          {{ 'Preview' | translate }}
          <span>({{ targetSizeLabel }})</span>
        </h6>
        <div
          class="preview-frame"
          [class.preview-frame--round]="roundCropper"
          [class.preview-frame--square]="allowLogoShapeSwitch && logoShapeMode === 'square' && !roundCropper"
          [class.preview-frame--transparent]="!roundCropper && canvasMode === 'transparent'"
          [class.preview-frame--canvas-dark]="!roundCropper && canvasMode === 'dark'"
          [class.preview-frame--canvas-light]="!roundCropper && canvasMode === 'light'"
          [style.aspect-ratio]="previewFrameAspectRatio"
        >
          <canvas
            #previewCanvas
            class="preview-frame__image"
            aria-hidden="true"
          ></canvas>
          <span *ngIf="!isEditorReady" class="preview-placeholder">
            {{ 'Preview' | translate }}
          </span>
        </div>
        <p *ngIf="maintainAspectRatio && aspectRatio" class="aspect-label">
          {{ 'Aspect ratio' | translate }}: {{ aspectRatioLabel }}
        </p>
        <div class="sidebar-section">
          <h6>{{ 'Uploaded Image Info' | translate }}</h6>
          <div class="info-row">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 7V3.5L18.5 9H13z"/></svg>
            <span>{{ 'Name:' | translate }}</span>
            <strong>{{ fileNameLabel }}</strong>
          </div>
          <div class="info-row">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/></svg>
            <span>{{ 'Size:' | translate }}</span>
            <strong>{{ fileSizeLabel }}</strong>
          </div>
          <div class="info-row">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 16H5V5h14v14zm-9.5-7.5L7 15h10l-3.5-4.5-2.5 3.01-1.5-2.01z"/></svg>
            <span>{{ 'Resolution:' | translate }}</span>
            <strong>{{ imageSizeLabel }}</strong>
          </div>
          <div class="info-row">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/></svg>
            <span>{{ 'Format:' | translate }}</span>
            <strong>{{ formatLabel }}</strong>
          </div>
        </div>
        <div class="sidebar-section">
          <h6>{{ 'Final Image Info' | translate }}</h6>
          <div class="info-row">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 16H5V5h14v14zm-9.5-7.5L7 15h10l-3.5-4.5-2.5 3.01-1.5-2.01z"/></svg>
            <span>{{ 'Resolution:' | translate }}</span>
            <strong>{{ targetSizeLabel }}</strong>
          </div>
          <div class="info-row">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/></svg>
            <span>{{ 'Size:' | translate }}</span>
            <strong>{{ finalImageSizeLabel }}</strong>
          </div>
        </div>
      </aside>
    </div>
  </div>

  <div *ngIf="showHelp" class="cropper-help-panel">
    <button
      type="button"
      class="help-close"
      aria-label="Close"
      (click)="toggleHelp()"
    >
      <span aria-hidden="true">×</span>
    </button>
    <h6>{{ 'How to crop and use the image' | translate }}</h6>
    <ul>
      <li>{{ 'Use Move Selection to drag the crop frame over the image.' | translate }}</li>
      <li>{{ 'Use Move Image to drag the image itself, even when clicking directly on it.' | translate }}</li>
      <li>{{ 'Use Re-upload to select a different image without closing the editor.' | translate }}</li>
      <li>{{ 'Use the zoom slider, mouse wheel, or corner handles to resize the image.' | translate }}</li>
      <li>{{ 'Drag the selection frame to position the crop area as desired.' | translate }}</li>
      <li>{{ 'Use the on-canvas arrow pad or keyboard arrow keys to nudge the image or selection by 1 pixel for precise alignment.' | translate }}</li>
      <li>{{ 'For logo uploads, use Square or Rectangle to choose a 250×250 or horizontal export size (client page preview is unchanged).' | translate }}</li>
      <li>{{ 'Use Light, Dark, or Transparent canvas icons (top-right) to preview how the logo looks on different backgrounds.' | translate }}</li>
      <li>{{ 'Keep the logo transparency appears only when the uploaded image has detected transparency. On Transparent canvas it is checked and locked.' | translate }}</li>
      <li>{{ 'Use Rotate Left or Rotate Right if the image orientation is incorrect.' | translate }}</li>
      <li>{{ 'Use Reset to restore the original zoom level and rotation.' | translate }}</li>
      <li>{{ 'The final image will be generated at the recommended output size shown in the preview panel.' | translate }}</li>
      <li>{{ 'Click Apply Crop to upload the cropped image.' | translate }}</li>
    </ul>
  </div>

  <div class="image-cropper-modal__footer">
    <button *ngIf="!showHelp" type="button" class="help-btn" (click)="toggleHelp()">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 18h2v-2h-2v2zm1-16a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm0-14a3 3 0 0 0-3 3h2a1 1 0 1 1 1 1c-1.1 0-2 .9-2 2v1h2v-1c1.66 0 3-1.34 3-3a3 3 0 0 0-3-3z"/></svg>
      {{ 'Help' | translate }}
    </button>
    <span *ngIf="showHelp" class="help-btn-spacer" aria-hidden="true"></span>
    <div class="footer-actions">
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        (click)="cancel()"
        [disabled]="isApplying"
      >
        {{ 'Cancel' | translate }}
      </button>
      <button
        type="button"
        class="btn btn-sm btn-primary"
        (click)="apply()"
        [disabled]="!isEditorReady || isApplying"
      >
        {{ isApplying ? ('Applying...' | translate) : ('Apply Crop' | translate) }}
      </button>
    </div>
  </div>
</div>
